import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import { Box } from '@/ui/layout';
import { ProgressBar } from '@/ui/linear-progress/linear-progress';
import { Typography } from '@/ui/typography/typography';
import { visuallyHidden } from '@/ui/style/default';
import type { SxStyles } from '@/ui/style/types';

import { NorthArrow, NorthPoleFlag } from '@/core/components/north-arrow/north-arrow';
import { Crosshair } from '@/core/components/crosshair/crosshair';
import { OverviewMap } from '@/core/components/overview-map/overview-map';
import { ClickMarker } from '@/core/components/click-marker/click-marker';
import { HoverTooltip } from '@/core/components/hover-tooltip/hover-tooltip';
import { DrawingHint } from '@/core/components/common/drawing-hint';
import type { MapViewer } from '@/geo/map/map-viewer';
import { getSxClasses } from './map-style';
import {
  useStoreMapInteraction,
  useStoreMapLoaded,
  useStoreMapNorthArrow,
  useStoreMapOverviewShouldBeVisible,
} from '@/core/stores/states/map-state';
import { useStoreGeoViewMapId } from '@/core/stores/geoview-store';
import { logger } from '@/core/utils/logger';

import { useStoreLayerAreLayersLoading } from '@/core/stores/states/layer-state';
import { getStoreAppIsCrosshairsActive, useStoreAppGeoviewHTMLElement } from '@/core/stores/states/app-state';
import { useUIController } from '@/core/controllers/use-controllers';
import { buildGVElementId, GV_DOM_SUFFIX } from '@/core/utils/dom-helper';

/** Props for the Map component. */
type MapProps = {
  /** The map viewer instance. */
  viewer: MapViewer;
};

/**
 * Creates the map component.
 *
 * @param props - The map component props
 * @returns The map component
 */
export function Map(props: MapProps): JSX.Element {
  // Log
  logger.logTraceRender('components/map/map');

  const { viewer } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const memoSxClasses = useMemo((): SxStyles => getSxClasses(theme), [theme]);

  // internal state - get ref to div element
  const mapElement = useRef<HTMLElement>(null);

  // get values from the store
  const mapId = useStoreGeoViewMapId();
  const overviewShouldBeVisible = useStoreMapOverviewShouldBeVisible();
  const northArrow = useStoreMapNorthArrow();
  const mapLoaded = useStoreMapLoaded();
  const mapInteraction = useStoreMapInteraction();
  const layersAreLoading = useStoreLayerAreLayersLoading();
  const geoviewHTMLElement = useStoreAppGeoviewHTMLElement();

  const uiController = useUIController();
  const [shortcutMessage, setShortcutMessage] = useState<string>('');

  // flag to check if map is initialized. we added to prevent double rendering in StrictMode
  const hasRun = useRef<boolean>(false);

  /**
   * Initializes the map viewer on mount.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('MAP - viewer');

    // GV Prevent double run, due to React's StrictMode in dev.
    if (!hasRun.current && mapElement.current) {
      hasRun.current = true;

      // Create map
      viewer.createMap(mapElement.current);
    }
    // NOTE: `viewer` is included for React exhaustive-deps compliance but does not change during component lifetime.
  }, [viewer]);

  /**
   * Global keyboard shortcut to activate crosshair and focus map.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('MAP - Crosshair shortcut listener');

    const handleGlobalShortcut = (event: KeyboardEvent): void => {
      // Ctrl+M (or Cmd+M on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm' && !event.shiftKey) {
        // Only activate if map interaction is not static
        if (mapInteraction !== 'static' && mapElement.current) {
          // Check if the currently focused element is within the GeoView app
          const { activeElement } = document;

          // Only respond if focus is within this specific map instance
          if (!activeElement || !geoviewHTMLElement?.contains(activeElement)) {
            return; // Focus is not in this map - let another map instance handle it
          }

          event.preventDefault();

          // Finish closing panels and restoring their focus before transferring focus to the map.
          flushSync((): void => {
            uiController.closeMapPopovers();
            setShortcutMessage('');
          });

          mapElement.current.focus({ preventScroll: true });
          if (!getStoreAppIsCrosshairsActive(mapId)) {
            uiController.setCrosshairActive(true);
          }
          setShortcutMessage(t('mapctrl.keyboardShortcut.focused'));
        }
      }
    };

    document.addEventListener('keydown', handleGlobalShortcut);

    return (): void => {
      document.removeEventListener('keydown', handleGlobalShortcut);
    };
  }, [mapInteraction, mapId, uiController, geoviewHTMLElement, t]);

  return (
    // ? the map is focusable and needs to be tabbable for keyboard navigation (only when interaction is dynamic)
    <Box
      id={buildGVElementId(mapId, GV_DOM_SUFFIX.mapTarget)}
      ref={mapElement}
      sx={memoSxClasses.mapContainer}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the map region is keyboard-operable (arrow-key panning, crosshair) when interaction is dynamic
      tabIndex={mapInteraction === 'static' ? -1 : 0}
      role="region"
      aria-label={t('map.container', { mapId })}
    >
      <Typography role="status" aria-live="polite" sx={visuallyHidden}>
        {shortcutMessage}
      </Typography>
      {mapLoaded && (
        <>
          {northArrow && <NorthArrow />}
          <NorthPoleFlag />
          <DrawingHint />
          <Crosshair mapTargetElement={mapElement.current!} />
          <ClickMarker />
          <HoverTooltip />
          {overviewShouldBeVisible && viewer.map && <OverviewMap i18n={viewer.getI18nInstance()} />}
        </>
      )}
      {layersAreLoading && (
        <Box sx={{ ...memoSxClasses.progressBar, bottom: mapInteraction === 'static' ? 0 : 40 }}>
          <ProgressBar aria-label={t('map.status.loadingLayers')} />
        </Box>
      )}
    </Box>
  );
}
